import { FormikHelpers } from 'formik';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { useCreateRaptorMultiDayJob } from '@/raptorjob/application/adapters/useCreateRaptorMultiDayJob';
import { useCreateSingleDayJob } from '@/raptorjob/application/adapters/useRaptorJobRest';
import { RaptorJobFormValues } from '../../../RaptorJobValidationSchema';
import { SelectedEquipment } from '../RaptorJobCreateModal';

interface UseRaptorJobMutationsProps {
  handleClose: () => void;
  openCreatedJob: (id: string) => void;
  selectedEquipment: SelectedEquipment | null;
}

export const useRaptorJobMutations = ({ handleClose, openCreatedJob, selectedEquipment }: UseRaptorJobMutationsProps) => {
  const { showSnackbar } = useSnackbar();
  const createMultiDayJob = useCreateRaptorMultiDayJob();
  const createJobMutation = useCreateSingleDayJob();

  const handleSubmit = async (values: RaptorJobFormValues, actions: FormikHelpers<RaptorJobFormValues>) => {
    actions.setSubmitting(true);

    try {
      if (values.recurrence === 'multi') {
        const resp = await createMultiDayJob.mutateAsync({
          customerId: values.customerId ?? null,
          jobSiteId: values.jobSiteId ?? null,
          jobTypeId: values.jobTypeId ?? null,
          jobDescription: values.jobDescription ?? '',
          recurrence: values.recurrence,
          startDate: values.startDate ?? null,
          estimatedDurationDays: values.estimatedDurationDays ?? null,
          endDate: values.endDate ?? null,
          startTime: values.startTime ?? null,
          endTime: values.endTime ?? null,
          showUpTime: values.showUpTime ?? null,
          onJobTime: values.onJobTime ?? null,
          notesForCrew: values.notesForCrew ?? '',
          jobNotes: values.jobNotes ?? '',
          estimates: values.estimates
        });
        showSnackbar('Job Created Successfully', `Job #${resp.id} has been created.`, 'success');
        openCreatedJob(resp.id);
        return;
      }

      const groups = values.groups && values.groups.length > 0 && selectedEquipment ? values.groups : values.groups;

      await createJobMutation.mutateAsync({ values, groups, idempotencyKey: undefined });
      showSnackbar('Job Created Successfully', `Job has been created.`, 'success');
      handleClose();
    } catch (error: unknown) {
      const errorObj = error as { error?: { details?: Array<{ path: string; reason: string }>; code?: string }; message?: string };
      const details: Array<{ path: string; reason: string }> | undefined = errorObj?.error?.details;
      if (Array.isArray(details)) {
        details.forEach((d) => {
          const path = (d.path || '').replace(/^\./, '');
          actions.setFieldError(path as string, d.reason || 'Invalid');
        });
      }
      showSnackbar(
        'Job Creation Failed',
        errorObj?.error?.code === 'VALIDATION_ERROR'
          ? 'Please review the highlighted fields.'
          : error instanceof Error
            ? error.message
            : 'An unexpected error occurred while creating the job. Please try again.',
        'error'
      );
    } finally {
      actions.setSubmitting(false);
    }
  };

  return {
    handleSubmit
  };
};
